import { PmsRiskResolution } from '../../../domain/pms-risk-resolution';
import { PmsRiskResolutionUtils } from '../../../util/pms-risk-resolution-utils';
import {
   ReportRiskResolutionState,
   ReportRowRiskResolutionState,
   ReportRowRiskResolutionStatus
} from '../models/report-row-risk-resolution';
import { ReportRowVm } from '../models/report-row-vm';

type RiskResolutionIndex = Map<string, PmsRiskResolution>;

export function attachReportRowRiskResolutions(
   rows: readonly ReportRowVm[],
   resolutions: readonly PmsRiskResolution[]
): ReportRowVm[] {
   const resolutionIndex = buildRiskResolutionIndex(resolutions);

   return rows.map((row) => ({
      ...row,
      riskResolution:
         buildReportRowRiskResolutionState(row, resolutionIndex) ?? undefined
   }));
}

export function clearReportRowRiskResolutions(
   rows: readonly ReportRowVm[]
): ReportRowVm[] {
   return rows.map((row) => ({
      ...row,
      riskResolution: undefined
   }));
}

/**
 * Only genuine one-to-one generated report rows are resolvable in V1.
 *
 * Grouped/calculated rows expose sourceRowIds and must not receive their own
 * durable resolution state.
 */
export function isReportRowRiskResolutionEligible(row: ReportRowVm): boolean {
   const rowId = row.rowId.trim();

   return (
      rowId.length > 0 &&
      rowId !== '__REPORT__' &&
      !row.sourceRowIds?.length &&
      row.risk?.hasRisk === true &&
      row.risk.riskCodes.length > 0
   );
}

function buildReportRowRiskResolutionState(
   row: ReportRowVm,
   resolutionIndex: RiskResolutionIndex
): ReportRowRiskResolutionState | null {
   if (!isReportRowRiskResolutionEligible(row) || !row.risk) {
      return null;
   }

   const risks: ReportRiskResolutionState[] = row.risk.riskCodes.map((riskCode) => {
      const resolution =
         resolutionIndex.get(
            PmsRiskResolutionUtils.buildIdentityKey(row.rowId, riskCode)
         ) ?? null;

      return {
         riskCode,
         isResolved: resolution?.isResolved === true,
         resolution
      };
   });

   const resolvedRiskCount = risks.filter((risk) => risk.isResolved).length;
   const totalRiskCount = risks.length;

   return {
      status: getResolutionStatus(resolvedRiskCount, totalRiskCount),
      totalRiskCount,
      resolvedRiskCount,
      unresolvedRiskCount: totalRiskCount - resolvedRiskCount,
      risks
   };
}

function buildRiskResolutionIndex(
   resolutions: readonly PmsRiskResolution[]
): RiskResolutionIndex {
   const index: RiskResolutionIndex = new Map();

   for (const resolution of resolutions) {
      const rowId = resolution.targetRowId.trim();
      const riskCode = PmsRiskResolutionUtils.normalizeRiskCode(resolution.riskCode);

      if (!rowId || !riskCode) {
         continue;
      }

      index.set(PmsRiskResolutionUtils.buildIdentityKey(rowId, riskCode), resolution);
   }

   return index;
}

function getResolutionStatus(
   resolvedRiskCount: number,
   totalRiskCount: number
): ReportRowRiskResolutionStatus {
   if (resolvedRiskCount === 0) {
      return 'UNRESOLVED';
   }

   if (resolvedRiskCount === totalRiskCount) {
      return 'RESOLVED';
   }

   return 'PARTIALLY_RESOLVED';
}
